import React, { useEffect } from 'react';
import { Col, Row, Typography } from 'antd';
import {
  gql,
  useQuery,
  useNavigate,
  useParams,
  useLocation,
} from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  AppContent,
  Banner,
  ErrorHandler,
  Icon,
  Link,
  LoadingState,
  Tabs,
} from '@common/components';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { ShiftTimesList } from '@scheduling/components';
import { OrganizationUnitDetails } from '@institutions/components';
import { IntegrationForm } from '@scheduling/components/admin-organization-unit-integration';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { buildDialogUrl } from '@common/services';

const adminOrgUnitViewQuery = gql`
  query AdminOrganizationUnitPageQuery($orgUnitId: ID!, $scope: String!) {
    organizationUnit(uuid: $orgUnitId, scope: $scope) {
      id
      name
      canUpdate {
        value
      }
    }
  }
`;

type adminOrgUnitViewQueryResponse = {
  organizationUnit: {
    id: string;
    name: string;
    canUpdate: {
      value: boolean;
    };
  };
};

type adminOrgUnitViewQueryVariables = {
  orgUnitId: string;
  scope: string;
};

const { Title } = Typography;

export function CannotEditUnitBanner() {
  return (
    <Row style={{ paddingTop: 24 }}>
      <Col xxl={{ span: 16 }} span={24}>
        <Banner
          type="info"
          content="You don't have permission to make edits."
          fontSize="16px"
        />
      </Col>
    </Row>
  );
}

export function AdminOrganizationUnitPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();

  const { organizationUnitId, tabKey } = useParams<{
    organizationUnitId: string;
    tabKey: 'unit-details' | 'shifts' | 'integration';
  }>();
  const handleTabChange = (key: string) =>
    navigate(location.pathname.replace(`/${tabKey}`, `/${key}`));

  const { data, error, loading } = useQuery<
    adminOrgUnitViewQueryResponse,
    adminOrgUnitViewQueryVariables
  >(adminOrgUnitViewQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      orgUnitId: organizationUnitId,
      scope: 'organization',
    },
  });

  useEffect(
    () =>
      setBreadcrumbs([
        {
          name: 'Admin',
          path: '/works/admin',
        },
        {
          name: 'Locations',
          path: ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE,
        },
        {
          name: data?.organizationUnit?.name,
          path: `${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/unit/${organizationUnitId}/${tabKey}`,
          privateInfo: true,
        },
      ]),
    [data?.organizationUnit?.name, organizationUnitId, tabKey]
  );

  if (error) {
    return <ErrorHandler error={error} log="AdminOrganizationUnitPage" />;
  }

  if (loading) {
    return <LoadingState />;
  }

  const showShiftTimesList =
    currentMembership.canShowOrganizationUnitShiftConfigs;
  const showIntegration = currentMembership.canShowOrganizationUnitIntegration;

  const canEdit = data?.organizationUnit?.canUpdate?.value || false;
  const isTypeShifts = tabKey === 'shifts';
  const integrationsEnabled = currentMembership.integrationsEnabled;

  const editDrawerUrl = buildDialogUrl('organization-unit-edit-drawer', {
    props: {
      organizationUnitId,
    },
  });

  return (
    <>
      <AppContent className="admin-organization-unit-view">
        <Title level={2} style={{ marginBottom: 24 }}>
          {data?.organizationUnit?.name}
          {canEdit && (
            <span style={{ marginLeft: 16 }}>
              <Link to={editDrawerUrl}>
                <Icon icon="pencil" size="xl" color="elm" />
              </Link>
            </span>
          )}
        </Title>
        <Tabs
          activeKey={tabKey}
          defaultActiveKey={tabKey}
          onTabClick={handleTabChange}
          tabBarGutter={0}
          tabBarExtraContent={
            isTypeShifts && (
              <div id="wk-portal-target:location-shift-times-list-extra-actions"></div>
            )
          }
        >
          <Tabs.Pane tab="Details" key="unit-details">
            {!canEdit && <CannotEditUnitBanner />}
            <OrganizationUnitDetails organizationUnitId={organizationUnitId} />
          </Tabs.Pane>
          {showShiftTimesList && (
            <Tabs.Pane tab="Shift Times" key="shifts">
              {!canEdit && <CannotEditUnitBanner />}
              <ShiftTimesList
                organizationUnitId={organizationUnitId}
                canEdit={canEdit}
                triggerPortal={isTypeShifts}
                integrationsEnabled={integrationsEnabled}
              />
            </Tabs.Pane>
          )}
          {showIntegration && (
            <Tabs.Pane tab="Integration" key="integration">
              {!canEdit && <CannotEditUnitBanner />}
              <IntegrationForm
                organizationUnitId={organizationUnitId}
                disabled={!canEdit}
              />
            </Tabs.Pane>
          )}
        </Tabs>
      </AppContent>
      <style jsx>{`
        :global(.admin-organization-unit-view .ant-tabs-tab) {
          font-weight: 500;
          line-height: 20px;
          letter-spacing: 1.4px;
          padding: 8px 16px 10px 16px;
          margin: 0;
        }
      `}</style>
    </>
  );
}
